import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import getInValidPostFields from '../../helpers/getInValidPostFields';
import { useFormComposer } from './FormComposerProvider';
import FormLoading from './FormLoading';
import GlobalFormBlocker from './GlobalFormBlocker';
import normalizeValidationErrors from './lib/normalizeValidationErrors';
import FormErrorsFormState from './FormErrorsFormState';
import { useFormDebug } from './FormDebugProvider';
import FormActionDialog from './FormActionDialog';
import FormWarnings from './FormWarnings';

/**
 * FormRenderer is a React component that renders a form using react-hook-form and MobX.
 * It provides form validation via Yup, error handling, and imperative methods for submit/reset.
 *
 * Props:
 * - navigate: Function to navigate to a different route.
 * - onSubmit: Async function called on form submission. Should return { nextValues, submitErrors }.
 * - children: Form fields/components to render inside the form.
 *
 * Features:
 * - Uses context from FormComposerProvider for default values and validation schema.
 * - Cleans up form data before submission (removes invalid fields, converts empty strings to null).
 * - Handles form errors and displays them using FormErrorTypeMessages and FormStateErrors.
 * - Exposes imperative methods: submit, reset, resetAndNavigate.
 * - Prompts user if there are unsaved changes when navigating away.
 */
const FormRenderer = forwardRef(
  ({ children, submitWithReadOnlyFields = false, dialog = false }, ref) => {
    const debug = useFormDebug();
    const {
      defaultValues: initialFormValues,
      schema,
      validations,
      formId: currentFormId,
      onSubmit,
      formWarnings: defaultFormWarnings = [],
      permissions = {},
    } = useFormComposer();

    const formRef = useRef();
    const formActionDialogRef = useRef();
    const parentRef = useRef();

    const [loading, setLoading] = useState(false);
    const [formWarnings, setFormWarnings] = useState(defaultFormWarnings || []);

    const methods = useForm({
      defaultValues: initialFormValues,
      ...(validations && { resolver: yupResolver(validations) }),
      shouldUnregister: false,
      mode: 'onBlur',
      reValidateMode: 'onBlur',
    });

    const {
      handleSubmit,
      reset,
      setError: setFormError,
      clearErrors,
    } = methods;

    const resetOptiions = {
      keepDirty: false,
      keepTouched: false,
    };

    const resetForm = () => {
      methods.reset(initialFormValues, resetOptiions);
    };

    const hasUnsavedChanges = () => methods.formState.isDirty;

    /*
      RHF + navigation race condition
      RHF reset is synchronous, but React state visibility is not.
      Navigation blockers run on the next render, not after your function finishes.
    */
    const delay = ms =>
      new Promise(resolve => {
        setTimeout(resolve, ms);
      });

    const onLocalSubmit = async (data, additional) => {
      debug.add('FormRenderer Submitted', data);
      debug.add('FormRenderer Additional', additional);
      if (!additional?.formId) {
        throw new Error(
          'Please add form id to your submission action button e.g form.current.submit(formId)',
        );
      }

      // The submisison will happen only when the form id matches the button in which the form resides
      if (currentFormId !== additional?.formId) {
        return;
      }

      clearErrors();

      const { defaultValues } = methods?.formState || { defaultValues: {} };

      try {
        if (!hasUnsavedChanges()) {
          formActionDialogRef?.current.init({
            message:
              'There is no information on this page that needs to be saved',
            buttons: [
              {
                label: 'Close',
                onClick: () => {
                  formActionDialogRef?.current.close();
                },
                variant: 'outlined',
              },
            ],
            width: 360,
            title: 'No Changes Found',
          });
          formActionDialogRef?.current?.open();
          return;
        }
        setLoading(true);
        const inValidFields = getInValidPostFields(
          {
            currentFormSchema: schema,
            initialValues: defaultValues,
          },
          submitWithReadOnlyFields,
        );

        // assign null to all empty string fields
        const currentPostData = Object.fromEntries(
          Object.entries(data).map(([key, value]) =>
            value === '' ? [key, null] : [key, value],
          ),
        );

        // Remove all fields with register false, or readOnly
        // This is for maintenance pages schema
        inValidFields.forEach(vf => {
          if (Object.prototype.hasOwnProperty.call(currentPostData, vf)) {
            delete currentPostData[vf];
          }
        });

        const {
          nextValues,
          errors: submitErrors,
          callback = null,
          warnings = [],
        } = await onSubmit(currentPostData, additional, permissions);

        if (Array.isArray(warnings) && warnings?.length > 0) {
          setFormWarnings(warnings);
          debug.add('FormRenderer warnings set', warnings);
        } else {
          setFormWarnings([]);
        }

        if (submitErrors) {
          const normalizedErrors = normalizeValidationErrors({
            errors: submitErrors,
            giveCount: false,
          });
          // Set Error in FormState
          normalizedErrors.forEach(field => {
            setFormError(field.path, {
              type: 'server',
              message: field.message,
            });
          });
          if (
            additional?.onFailure &&
            typeof additional?.onFailure === 'function'
          ) {
            additional?.onFailure();
          }
        } else {
          reset(nextValues, resetOptiions);
          await delay(10);
          clearErrors();
          if (callback && typeof callback === 'function') {
            callback();
          }
          if (
            additional?.onSuccess &&
            typeof additional?.onSuccess === 'function'
          ) {
            additional?.onSuccess();
          }
        }
      } catch (err) {
        setFormError('', {
          type: 'server',
          message: err?.message,
        });
        if (
          additional?.onFailure &&
          typeof additional?.onFailure === 'function'
        ) {
          additional?.onFailure();
        }
      }
      setLoading(false);
    };

    /*
     Use this if you dont want to submit the form and go through validations, 
    but just want to execute action, and then get teh form and default values to stable state
    */
    const onFormButtonAction = async (formId, callbackActionFn) => {
      debug.add('FormRenderer onFormButtonAction called', formId);
      if (!formId) {
        throw new Error(
          'Please add form id to your form button action e.g form.current.onFormButtonAction(formId, callback)',
        );
      }

      // The submisison will happen only when the form id matches the button in which the form resides
      if (currentFormId !== formId) {
        return;
      }

      clearErrors();

      try {
        setLoading(true);

        const {
          nextValues,
          errors: submitErrors,
          callback = null,
          warnings = [],
        } = await callbackActionFn();

        if (Array.isArray(warnings) && warnings?.length > 0) {
          setFormWarnings(warnings);
          debug.add('FormRenderer warnings set', warnings);
        } else {
          setFormWarnings([]);
        }
        if (submitErrors) {
          const normalizedErrors = normalizeValidationErrors({
            errors: submitErrors,
            giveCount: false,
          });
          // Set Error in FormState
          normalizedErrors.forEach(field => {
            setFormError(field.path, {
              type: 'server',
              message: field.message,
            });
          });
          if (callback && typeof callback === 'function') {
            callback();
          }
        } else {
          reset(nextValues, resetOptiions);
          await delay(10);
          clearErrors();
          if (callback && typeof callback === 'function') {
            callback();
          }
        }
      } catch (err) {
        setFormError('', {
          type: 'server',
          message: err?.message,
        });
      }
      setLoading(false);
    };

    useImperativeHandle(ref, () => ({
      submit: (additional = {}) => {
        if (currentFormId === additional?.formId) {
          return handleSubmit(
            submittedData => onLocalSubmit(submittedData, additional),
            () => {
              formActionDialogRef?.current?.close();
            },
          )();
        }
        return null;
      },
      onFormButtonAction,
      getDefaultValues: () => methods?.formState?.defaultValues || {},
      reset: () => {
        clearErrors();
        reset();
      },
      clearErrors: () => clearErrors(),
      isDirty: () => hasUnsavedChanges(),
      getActionDialog: () => formActionDialogRef?.current,
    }));

    debug.log();

    return (
      <FormProvider {...methods}>
        <form
          ref={formRef}
          autoComplete="off"
          noValidate
          onSubmit={() => undefined}>
          <GlobalFormBlocker resetForm={resetForm} />
          {loading && <FormLoading>Processing form</FormLoading>}
          {!loading && (
            <Box>
              <Box ref={parentRef}>
                <FormErrorsFormState dialog={dialog} parentRef={parentRef} />
              </Box>
              <Box>
                <FormWarnings warnings={formWarnings} />
              </Box>
              {children}
            </Box>
          )}
        </form>
        <FormActionDialog ref={formActionDialogRef} />
      </FormProvider>
    );
  },
);

export default FormRenderer;
